
import { supabase } from '@/integrations/supabase/client';

// Types for our API responses
export interface Creator {
  id: string;
  name: string;
  avatar: string;
  subscribers: number;
  isSubscribed: boolean;
}

export interface Video {
  id: string;
  title: string;
  thumbnail: string;
  channelName: string;
  channelAvatar: string;
  channelId: string;
  views: number;
  published: string;
  publishedDate: string;
  duration: string;
  video_id?: string; // Ajout d'un champ optionnel pour la compatibilité
}

export interface YouTubeQuotaData {
  quotaPercentage: number;
  queriesUsed: number;
  isExceeded: boolean;
}

// YouTube API service
export const api = {
  // Récupérer la clé API YouTube de l'utilisateur
  getUserApiKey: async (): Promise<string | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('youtube_api_key')
        .eq('id', user.id)
        .single();
      
      if (error || !data) {
        console.error('Erreur lors de la récupération de la clé API:', error);
        return null;
      }
      
      // Vérifier si la clé API existe et n'est pas vide
      if (!data.youtube_api_key || data.youtube_api_key.trim() === '') {
        console.log('Aucune clé API YouTube personnelle trouvée pour cet utilisateur');
        return null;
      }
      
      console.log('Clé API YouTube personnelle trouvée');
      return data.youtube_api_key;
    } catch (error) {
      console.error('Erreur lors de la récupération de la clé API:', error);
      return null;
    }
  },
  
  // Obtenir la clé API à utiliser (uniquement la clé de l'utilisateur, pas de clé par défaut)
  getApiKey: async (): Promise<string | null> => {
    const userKey = await api.getUserApiKey();
    
    if (userKey) {
      console.log('Utilisation de la clé API YouTube personnelle de l\'utilisateur');
      return userKey;
    } else {
      console.log('Aucune clé API YouTube personnelle disponible');
      return null;
    }
  },
  
  // Récupérer les données réelles d'utilisation du quota de l'API YouTube
  getYouTubeQuotaUsage: async (): Promise<YouTubeQuotaData | null> => {
    try {
      const apiKey = await api.getApiKey();
      
      if (!apiKey) {
        return null;
      }
      
      console.log('Test de quota YouTube en temps réel...');
      
      try {
        // Faire plusieurs tests pour vérifier le statut du quota
        // Test 1: Simple channel request (coût: 1 unité)
        const testResponse = await fetch(
          `https://www.googleapis.com/youtube/v3/channels?part=id&id=UC_x5XG1OV2P6uZZ5FSM9Ttw&key=${apiKey}`
        );
        
        // Si la réponse est 403, vérifier si c'est un problème de quota
        if (testResponse.status === 403) {
          const errorData = await testResponse.json();
          console.log('Erreur API détectée:', errorData);
          
          if (errorData.error && errorData.error.errors) {
            const quotaError = errorData.error.errors.find((e: any) => 
              e.reason === 'quotaExceeded' || 
              e.reason === 'dailyLimitExceeded' ||
              e.domain === 'usageLimits'
            );
            
            if (quotaError) {
              console.log('Quota YouTube dépassé détecté');
              return {
                quotaPercentage: 100,
                queriesUsed: 10000,
                isExceeded: true
              };
            }
          }
          
          // Autre erreur 403 (probablement API key invalide)
          console.log('Erreur 403 non liée au quota');
          return {
            quotaPercentage: 0,
            queriesUsed: 0,
            isExceeded: false
          };
        }
        
        // Si la réponse est OK, faire un test plus gourmand pour estimer l'usage
        if (testResponse.ok) {
          console.log('Premier test réussi, test d\'estimation du quota...');
          
          // Test plus gourmand pour voir si on approche de la limite
          try {
            const heavyTestResponse = await fetch(
              `https://www.googleapis.com/youtube/v3/search?part=snippet&maxResults=50&q=test&type=video&key=${apiKey}`
            );
            
            if (heavyTestResponse.status === 403) {
              const errorData = await heavyTestResponse.json();
              if (errorData.error && errorData.error.errors) {
                const quotaError = errorData.error.errors.find((e: any) => 
                  e.reason === 'quotaExceeded' || 
                  e.reason === 'dailyLimitExceeded'
                );
                
                if (quotaError) {
                  // On peut faire des requêtes simples mais pas complexes
                  console.log('Quota presque épuisé');
                  return {
                    quotaPercentage: 95,
                    queriesUsed: 9500,
                    isExceeded: false
                  };
                }
              }
            }
            
            if (heavyTestResponse.ok) {
              // Tout fonctionne bien, quota probablement faible
              console.log('Tests réussis, quota disponible');
              return {
                quotaPercentage: 20,
                queriesUsed: 2000,
                isExceeded: false
              };
            }
          } catch (error) {
            console.log('Test gourmand échoué, mais quota simple OK');
          }
          
          // Test simple OK, mais on ne peut pas faire le test gourmand
          return {
            quotaPercentage: 15,
            queriesUsed: 1500,
            isExceeded: false
          };
        }
        
        // Autre code de statut
        console.log('Statut inattendu:', testResponse.status);
        return {
          quotaPercentage: 0,
          queriesUsed: 0,
          isExceeded: false
        };
        
      } catch (error) {
        console.error('Erreur lors du test API:', error);
        
        // Si l'erreur contient des mots-clés de quota
        const errorMessage = error.toString().toLowerCase();
        if (errorMessage.includes('quota') || errorMessage.includes('limit')) {
          return {
            quotaPercentage: 100,
            queriesUsed: 10000,
            isExceeded: true
          };
        }
        
        return {
          quotaPercentage: 0,
          queriesUsed: 0,
          isExceeded: false
        };
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des données de quota YouTube:', error);
      return null;
    }
  },
  
  // Get all creators (top YouTube channels)
  getCreators: async (): Promise<Creator[]> => {
    try {
      const API_KEY = await api.getApiKey();
      
      if (!API_KEY) {
        throw new Error('Veuillez ajouter votre clé API YouTube dans les paramètres');
      }
      
      // Use the Edge Function to track quota usage
      const response = await supabase.functions.invoke('youtube-cache', {
        body: {
          action: 'getTrending',
          apiKey: API_KEY
        }
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to fetch creators');
      }

      // Extract unique channels from the trending videos
      const videos = response.data || [];
      const uniqueChannels = new Map();
      
      videos.forEach((video: any) => {
        if (!uniqueChannels.has(video.channel_id)) {
          uniqueChannels.set(video.channel_id, {
            id: video.channel_id,
            name: video.channel_name,
            avatar: video.channel_avatar,
            subscribers: 0, // We'll need to fetch this separately if needed
            isSubscribed: false
          });
        }
      });
      
      return Array.from(uniqueChannels.values()).slice(0, 15);
    } catch (error) {
      console.error('Error fetching creators:', error);
      return [];
    }
  },
  
  // Get subscribed creators
  getSubscribedCreators: async (): Promise<Creator[]> => {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      
      if (!user) {
        return [];
      }
      
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (!userProfile || !userProfile.subscriptions || userProfile.subscriptions.length === 0) {
        return [];
      }
      
      const subscribedIds = userProfile.subscriptions;
      
      const API_KEY = await api.getApiKey();
      if (!API_KEY) {
        throw new Error('Veuillez ajouter votre clé API YouTube dans les paramètres');
      }
      
      // Fetch each subscribed channel
      const promises = subscribedIds.map(async (channelId: string) => {
        const response = await fetch(
          `https://www.googleapis.com/youtube/v3/channels?part=snippet,statistics&id=${channelId}&key=${API_KEY}`
        );
        
        if (!response.ok) {
          return null;
        }
        
        const data = await response.json();
        if (!data.items || data.items.length === 0) {
          return null;
        }
        
        const item = data.items[0];
        return {
          id: item.id,
          name: item.snippet.title,
          avatar: item.snippet.thumbnails.medium.url,
          subscribers: parseInt(item.statistics.subscriberCount, 10),
          isSubscribed: true
        };
      });
      
      const results = await Promise.all(promises);
      return results.filter(Boolean) as Creator[];
    } catch (error) {
      console.error('Error fetching subscribed creators:', error);
      return [];
    }
  },
  
  // Get videos (trending videos)
  getVideos: async (): Promise<Video[]> => {
    try {
      const API_KEY = await api.getApiKey();
      if (!API_KEY) {
        throw new Error('Veuillez ajouter votre clé API YouTube dans les paramètres');
      }
      
      // Use the Edge Function to get trending videos and track quota
      const response = await supabase.functions.invoke('youtube-cache', {
        body: {
          action: 'getTrending',
          apiKey: API_KEY
        }
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to fetch videos');
      }

      const videos = response.data || [];
      
      // Transform cached video data to our Video format
      return videos.map((item: any) => ({
        id: item.video_id,
        title: item.title,
        thumbnail: item.thumbnail,
        channelName: item.channel_name,
        channelAvatar: item.channel_avatar,
        channelId: item.channel_id,
        views: item.views || 0,
        published: item.published,
        publishedDate: item.published_date,
        duration: item.duration,
        video_id: item.video_id
      }));
    } catch (error) {
      console.error('Error fetching videos:', error);
      return [];
    }
  },
  
  // Get videos from subscribed creators
  getSubscribedVideos: async (): Promise<Video[]> => {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      
      if (!user) {
        return [];
      }
      
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (!userProfile || !userProfile.subscriptions || userProfile.subscriptions.length === 0) {
        return [];
      }
      
      const subscribedIds = userProfile.subscriptions;
      
      const API_KEY = await api.getApiKey();
      if (!API_KEY) {
        throw new Error('Veuillez ajouter votre clé API YouTube dans les paramètres');
      }
      
      // For each channel, get their recent videos
      const promises = subscribedIds.map(async (channelId: string) => {
        const response = await fetch(
          `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${channelId}&maxResults=5&order=date&type=video&key=${API_KEY}`
        );
        
        if (!response.ok) {
          return [];
        }
        
        const data = await response.json();
        if (!data.items || data.items.length === 0) {
          return [];
        }
        
        // Get detailed video info for each video
        const videoIds = data.items.map((item: any) => item.id.videoId).join(',');
        const videoResponse = await fetch(
          `https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails,statistics&id=${videoIds}&key=${API_KEY}`
        );
        
        if (!videoResponse.ok) {
          return [];
        }
        
        const videoData = await videoResponse.json();
        
        // Get channel info
        const channelResponse = await fetch(
          `https://www.googleapis.com/youtube/v3/channels?part=snippet&id=${channelId}&key=${API_KEY}`
        );
        
        if (!channelResponse.ok) {
          return [];
        }
        
        const channelData = await channelResponse.json();
        const channel = channelData.items[0];
        
        return videoData.items.map((item: any) => {
          // Format view count
          const viewCount = parseInt(item.statistics.viewCount, 10);
          
          // Format publication date
          const publishedAt = new Date(item.snippet.publishedAt);
          const now = new Date();
          const diffTime = Math.abs(now.getTime() - publishedAt.getTime());
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); // Utilisation de Math.floor au lieu de Math.ceil
          
          let published = '';
          if (diffDays < 1) {
            // Pour les vidéos de moins d'un jour, afficher en heures
            const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
            if (diffHours === 0) {
              // Si moins d'une heure, afficher en minutes
              const diffMinutes = Math.floor(diffTime / (1000 * 60));
              published = `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;
            } else {
              published = `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
            }
          } else if (diffDays === 1) {
            published = '1 day ago';
          } else if (diffDays < 7) {
            published = `${diffDays} days ago`;
          } else if (diffDays < 30) {
            const diffWeeks = Math.floor(diffDays / 7);
            published = `${diffWeeks} ${diffWeeks === 1 ? 'week' : 'weeks'} ago`;
          } else if (diffDays < 365) {
            const diffMonths = Math.floor(diffDays / 30);
            published = `${diffMonths} ${diffMonths === 1 ? 'month' : 'months'} ago`;
          } else {
            const diffYears = Math.floor(diffDays / 365);
            published = `${diffYears} ${diffYears === 1 ? 'year' : 'years'} ago`;
          }
          
          // Format duration
          const duration = item.contentDetails.duration; // PT1H30M20S format
          let formattedDuration = '';
          
          const hours = duration.match(/(\d+)H/);
          const minutes = duration.match(/(\d+)M/);
          const seconds = duration.match(/(\d+)S/);
          
          if (hours) formattedDuration += `${hours[1]}:`;
          formattedDuration += `${minutes ? minutes[1].padStart(2, '0') : '00'}:`;
          formattedDuration += seconds ? seconds[1].padStart(2, '0') : '00';
          
          return {
            id: item.id,
            title: item.snippet.title,
            thumbnail: item.snippet.thumbnails.medium.url,
            channelName: item.snippet.channelTitle,
            channelAvatar: channel.snippet.thumbnails.default.url,
            channelId: item.snippet.channelId,
            views: viewCount,
            published,
            publishedDate: publishedAt.toISOString().split('T')[0],
            duration: formattedDuration
          };
        });
      });
      
      const results = await Promise.all(promises);
      return results.flat();
    } catch (error) {
      console.error('Error fetching subscribed videos:', error);
      return [];
    }
  },
  
  // Get daily videos (videos published today)
  getDailyVideos: async (selectedDate?: Date): Promise<Video[]> => {
    try {
      const API_KEY = await api.getApiKey();
      if (!API_KEY) {
        throw new Error('Veuillez ajouter votre clé API YouTube dans les paramètres');
      }
      
      // Convert selected date to ISO string
      const dateStr = selectedDate 
        ? selectedDate.toISOString().split('T')[0] 
        : new Date().toISOString().split('T')[0];
      
      // Fetch trending videos for the date (as YouTube API doesn't support filtering by exact date)
      const videos = await api.getVideos();
      
      // Filter videos by the selected date
      return videos.filter(video => 
        video.publishedDate === dateStr || 
        (dateStr === new Date().toISOString().split('T')[0] && video.published.includes('hours ago'))
      );
    } catch (error) {
      console.error('Error fetching daily videos:', error);
      return [];
    }
  },

  // Subscribe/unsubscribe to a creator
  toggleSubscription: async (creatorId: string): Promise<Creator[]> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User must be logged in to subscribe/unsubscribe');
      }
      
      // Get current user profile
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (profileError && profileError.code !== 'PGRST116') {
        throw new Error('Error fetching user profile');
      }
      
      // Create or update subscriptions array
      let subscriptions = userProfile?.subscriptions || [];
      
      if (!Array.isArray(subscriptions)) {
        subscriptions = [];
      }
      
      const isSubscribed = subscriptions.includes(creatorId);
      
      if (isSubscribed) {
        // Unsubscribe
        subscriptions = subscriptions.filter(id => id !== creatorId);
      } else {
        // Subscribe
        subscriptions.push(creatorId);
      }
      
      // Update profile with new subscriptions
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ subscriptions })
        .eq('id', user.id);
      
      if (updateError) {
        throw new Error('Error updating subscriptions');
      }
      
      // Return updated creators list
      const API_KEY = await api.getApiKey();
      if (!API_KEY) {
        throw new Error('Veuillez ajouter votre clé API YouTube dans les paramètres');
      }
      
      // Fetch channel info
      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/channels?part=snippet,statistics&id=${creatorId}&key=${API_KEY}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch channel information');
      }
      
      const data = await response.json();
      if (!data.items || data.items.length === 0) {
        throw new Error('Channel not found');
      }
      
      const item = data.items[0];
      
      return [{
        id: item.id,
        name: item.snippet.title,
        avatar: item.snippet.thumbnails.medium.url,
        subscribers: parseInt(item.statistics.subscriberCount, 10),
        isSubscribed: !isSubscribed
      }];
    } catch (error) {
      console.error('Error toggling subscription:', error);
      return [];
    }
  },
  
  // Search YouTube creators
  searchYouTubeCreators: async (query: string): Promise<Creator[]> => {
    try {
      const API_KEY = await api.getApiKey();
      if (!API_KEY) {
        throw new Error('Veuillez ajouter votre clé API YouTube dans les paramètres');
      }
      
      if (!query.trim()) {
        return [];
      }
      
      // Search for channels on YouTube
      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/search?part=snippet&q=${encodeURIComponent(query)}&type=channel&maxResults=10&key=${API_KEY}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to search YouTube channels');
      }
      
      const data = await response.json();
      
      if (!data.items || data.items.length === 0) {
        return [];
      }
      
      // Get detailed channel info for each result
      const channelIds = data.items.map((item: any) => item.snippet.channelId).join(',');
      
      const channelsResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/channels?part=snippet,statistics&id=${channelIds}&key=${API_KEY}`
      );
      
      if (!channelsResponse.ok) {
        throw new Error('Failed to fetch channel details');
      }
      
      const channelsData = await channelsResponse.json();
      
      if (!channelsData.items || channelsData.items.length === 0) {
        return [];
      }
      
      // Get user's subscriptions
      const { data: { user } } = await supabase.auth.getUser();
      let subscriptions: string[] = [];
      
      if (user) {
        const { data: userProfile } = await supabase
          .from('profiles')
          .select('subscriptions')
          .eq('id', user.id)
          .single();
        
        if (userProfile && userProfile.subscriptions) {
          subscriptions = userProfile.subscriptions;
        }
      }
      
      // Transform to our format and sort by subscribers (most popular first)
      return channelsData.items
        .map((item: any) => ({
          id: item.id,
          name: item.snippet.title,
          avatar: item.snippet.thumbnails.medium.url,
          subscribers: parseInt(item.statistics.subscriberCount, 10),
          isSubscribed: subscriptions.includes(item.id)
        }))
        .sort((a: Creator, b: Creator) => b.subscribers - a.subscribers);
    } catch (error) {
      console.error('Error searching YouTube creators:', error);
      return [];
    }
  },

  // Get video details
  getVideoDetails: async (videoId: string) => {
    try {
      const API_KEY = await api.getApiKey();
      if (!API_KEY) {
        throw new Error('Veuillez ajouter votre clé API YouTube dans les paramètres');
      }
      
      // Get detailed video info
      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails,statistics&id=${videoId}&key=${API_KEY}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch video details');
      }
      
      const data = await response.json();
      if (!data.items || data.items.length === 0) {
        throw new Error('Video not found');
      }
      
      const video = data.items[0];
      
      // Get channel info
      const channelResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/channels?part=snippet,statistics&id=${video.snippet.channelId}&key=${API_KEY}`
      );
      
      if (!channelResponse.ok) {
        throw new Error('Failed to fetch channel details');
      }
      
      const channelData = await channelResponse.json();
      if (!channelData.items || channelData.items.length === 0) {
        throw new Error('Channel not found');
      }
      
      const channel = channelData.items[0];
      
      // Format publication date
      const publishedAt = new Date(video.snippet.publishedAt);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - publishedAt.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      let published = '';
      if (diffDays < 1) {
        const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
        published = `${diffHours} hours ago`;
      } else if (diffDays === 1) {
        published = '1 day ago';
      } else if (diffDays < 7) {
        published = `${diffDays} days ago`;
      } else if (diffDays < 30) {
        const diffWeeks = Math.floor(diffDays / 7);
        published = `${diffWeeks} ${diffWeeks === 1 ? 'week' : 'weeks'} ago`;
      } else if (diffDays < 365) {
        const diffMonths = Math.floor(diffDays / 30);
        published = `${diffMonths} ${diffMonths === 1 ? 'month' : 'months'} ago`;
      } else {
        const diffYears = Math.floor(diffDays / 365);
        published = `${diffYears} ${diffYears === 1 ? 'year' : 'years'} ago`;
      }
      
      return {
        id: video.id,
        title: video.snippet.title,
        description: video.snippet.description,
        thumbnail: video.snippet.thumbnails.high.url,
        channelName: video.snippet.channelTitle,
        channelAvatar: channel.snippet.thumbnails.default.url,
        channelId: video.snippet.channelId,
        channelSubscribers: parseInt(channel.statistics.subscriberCount, 10),
        views: parseInt(video.statistics.viewCount, 10),
        likes: parseInt(video.statistics.likeCount, 10),
        dislikes: 0, // YouTube API no longer provides dislikes
        comments: parseInt(video.statistics.commentCount, 10),
        published,
        publishedDate: publishedAt.toISOString().split('T')[0],
        videoUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1&controls=1`
      };
    } catch (error) {
      console.error('Error fetching video details:', error);
      throw error;
    }
  }
};
